import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { partnerClientActions } from '../../actions/partner-client.actions';
import {
    IPartnerClientWithSecret,
    PARTNER_SCOPE_OPTIONS,
    PartnerScope,
} from '../../models/outbound/partner-client.model';
import { PartnerClientProvider } from '../../provider/partner-client.provider';

type CreateView = 'form' | 'secret';

/**
 * Two steps: name + scopes, then the one-time credential reveal. The secret
 * arrives via the actions stream and lives ONLY in this component — closing
 * the modal is the last time anyone sees it (§2).
 */
@Component({
    selector: 'aumico-partner-client-create-modal',
    templateUrl: './partner-client-create-modal.component.html',
    styleUrls: ['./partner-client-create-modal.component.scss'],
})
export class PartnerClientCreateModalComponent implements OnDestroy {
    @Output() public closed = new EventEmitter<void>();

    public view: CreateView = 'form';
    public displayName = '';
    public selectedScopes = new Set<PartnerScope>();
    public readonly scopeOptions = PARTNER_SCOPE_OPTIONS;

    public created: IPartnerClientWithSecret | null = null;
    public copiedField: 'clientId' | 'clientSecret' | null = null;

    public mutating$ = this.partnerClientProvider.isMutating$();
    public error$ = this.partnerClientProvider.getError$();

    private readonly destroy$ = new Subject<void>();

    public constructor(
        private readonly partnerClientProvider: PartnerClientProvider,
        actions$: Actions,
    ) {
        actions$
            .pipe(ofType(partnerClientActions.createClientSuccess), takeUntil(this.destroy$))
            .subscribe(({ client }) => {
                this.created = client;
                this.view = 'secret';
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get canSubmit(): boolean {
        return this.displayName.trim().length > 0 && this.selectedScopes.size > 0;
    }

    public toggleScope(scope: PartnerScope): void {
        if (this.selectedScopes.has(scope)) {
            this.selectedScopes.delete(scope);
        } else {
            this.selectedScopes.add(scope);
        }
    }

    public onSubmit(): void {
        if (!this.canSubmit) {
            return;
        }
        this.partnerClientProvider.createClient({
            displayName: this.displayName.trim(),
            scopes: [...this.selectedScopes],
        });
    }

    public onCopy(field: 'clientId' | 'clientSecret'): void {
        const value = field === 'clientId' ? this.created?.clientId : this.created?.clientSecret;
        if (value == null) {
            return;
        }
        navigator.clipboard.writeText(value).catch(() => undefined);
        this.copiedField = field;
    }

    public onClose(): void {
        this.closed.emit();
    }
}
